import { Label } from "../UI/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../UI/dropdown-menu";
import { countries } from "../Enums/DataEnums";
import { useSelector } from "react-redux";

const MobileInput = ({
    id,
    fieldID,
    label,
    placeholder,
    value,
    onChange,
    selectedCountryCode,
    onCountryCodeChange,
    onKeyPress,
    required = false,
    className = "space-y-1.5",
    disabled = false,
    autoFocus = false
}) => {
    const agencyData = useSelector((state) => state.agencyData.data)
    const agencyCC = agencyData?.CC || "91";
    const selectedCountry = countries.find(
        (c) => c.CC === selectedCountryCode
    ) || countries.find(c => c.CC === agencyCC.toString()); 

    // Determine max length based on country code
    const getMaxLength = (countryCode) => {
        const country = countries.find(c => c.CC === countryCode);
        const cc = country?.CC || "91";
        return cc === "91" ? 10 : 15; // 10 for India (+91), 15 for others
    };

    const maxLength = getMaxLength(selectedCountryCode);

    // Clean and parse the pasted phone number
    const parsePhoneNumber = (input) => {
        if (!input) return { number: value, countryCode: selectedCountryCode };

        // Preserve the input for country code detection
        let countryCode = selectedCountryCode;
        let number = input;

        // Check for country code (e.g., +1, +91)
        const countryCodeMatch = input.match(/^\+(\d{1,3})/);
        if (countryCodeMatch) {
            const potentialCC = countryCodeMatch[1];
            const country = countries.find(c => c.CC === potentialCC);
            if (country) {
                countryCode = country.CountryID;
                // Remove the country code and the '+' from the input
                number = input.replace(`+${potentialCC}`, '');
            }
        }

        // Remove all non-digit characters from the number
        number = number.replace(/\D/g, '');

        // Trim to maxLength if necessary
        if (number.length > maxLength) {
            number = number.slice(0, maxLength);
        }

        return { number, countryCode };
    };

    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        // Only allow numeric input for direct typing
        if (/^\d*$/.test(inputValue)) {
            onChange(e);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text').trim(); // Trim the pasted text
        const { number, countryCode } = parsePhoneNumber(pastedText);

        // Update the input value
        onChange({ target: { name: fieldID, value: number } });

        // Update the country code if it was detected
        if (countryCode && countryCode !== selectedCountryCode) {
            onCountryCodeChange(countryCode);
            const newCountry = countries.find(c => c.CountryID === countryCode);
            if (newCountry) {
                // Trigger country change in the parent form
                onChange({
                    target: {
                        name: 'country',
                        value: newCountry.Country
                    }
                });
                if (newCountry.Country !== "India") {
                    onChange({
                        target: {
                            name: 'state',
                            value: ""
                        }
                    });
                }
            }
        }
    };

    return (
        <div key={fieldID} className={className}>
            <Label
                htmlFor={fieldID}
                required={required}
            >
                {label}
            </Label>
            <div className="flex w-full h-11 bg-white sahi-input sahi-radius overflow-hidden">
                <DropdownMenu>
                    <DropdownMenuTrigger
                        disabled={disabled}
                        className={`h-11 w-12 flex items-center justify-center px-3 border-r border-gray-300 shrink-0 focus:outline-none
                        ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-background"}`}
                        tabIndex={-1}
                    >
                        <span className="text-sm">+{selectedCountry?.CC || "91"}</span>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent className="z-[10050] w-64 max-h-[300px] overflow-y-auto bg-white border border-gray-200 shadow-lg scrollbar-hide">
                        {countries.map((country) => (
                            <DropdownMenuItem
                                key={country.CountryID}
                                className="flex items-center justify-between px-3 py-2 cursor-pointer"
                                onSelect={() => {
                                    onCountryCodeChange(country.CC);
                                    const newCountry = countries.find(c => c.CountryID === country.CountryID);
                                    if (newCountry) {
                                        onChange({
                                            target: {
                                                name: 'country',
                                                value: newCountry.Country
                                            }
                                        });
                                        if (newCountry.Country !== "India") {
                                            onChange({
                                                target: {
                                                    name: 'state',
                                                    value: ""
                                                }
                                            });
                                        }
                                    }
                                }}
                            >
                                <span>{country.Country}</span>
                                <span className="text-muted-foreground">
                                    (+{country.CC})
                                </span>
                            </DropdownMenuItem>
                        ))}
                    </DropdownMenuContent>
                </DropdownMenu>
                <input
                    id={id || fieldID}
                    name="mobile"
                    type="tel"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={maxLength}
                    value={value}
                    onChange={handleInputChange}
                    onPaste={handlePaste}
                    onKeyPress={onKeyPress}
                    placeholder={placeholder}
                    tabIndex={0}
                    autoFocus={autoFocus}
                    className={`flex-1 border-0 focus:ring-0 focus:outline-none px-3 py-2 h-full placeholder:text-muted-foreground text-sm min-w-0
                        ${disabled ? "bg-gray-100 cursor-not-allowed opacity-60 text-gray-400" : "bg-transparent"}`}
                    disabled={disabled}
                />
            </div>
        </div>
    );
};

export default MobileInput;